'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useMemo, type CSSProperties, type MouseEvent, type ReactNode } from 'react';

type Props = {
  linkName: string;
  href: string;
  openBehavior?: string | null;
  showPopupNote?: boolean | null;
  popupNoteHtml?: string | null;
  clipboardText?: string | null;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
  onNavigate?: () => void;
};

type NoteWindowOptions = {
  destinationHref?: string | null;
  openDestinationInNewTab?: boolean;
};

function hasPopupNoteContent(value?: string | null) {
  if (!value) return false;
  const text = value
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return text.length > 0;
}

function hasClipboardText(value?: string | null) {
  return typeof value === 'string' && value.trim().length > 0;
}

function legacyCopyToClipboard(text: string) {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', 'true');
  textArea.style.position = 'fixed';
  textArea.style.top = '-9999px';
  textArea.style.left = '-9999px';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  textArea.setSelectionRange(0, textArea.value.length);
  let success = false;
  try {
    success = document.execCommand('copy');
  } catch {
    success = false;
  }
  document.body.removeChild(textArea);
  return success;
}

async function writeClipboardText(value?: string | null) {
  if (!hasClipboardText(value)) return false;
  const text = String(value);
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {}
  }
  return legacyCopyToClipboard(text);
}

function openExternalNoteWindow(
  linkName: string,
  popupNoteHtml?: string | null,
  options?: NoteWindowOptions,
) {
  const popup = window.open(
    'about:blank',
    `_bsn_note_${Date.now()}`,
    'popup=yes,width=760,height=620,resizable=yes,scrollbars=yes',
  );
  if (!popup) return false;

  try {
    popup.document.open();
    popup.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width,initial-scale=1" />
    <title></title>
    <style>
      :root { color-scheme: light dark; }
      body {
        margin: 0;
        font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
        background: #0f1117;
        color: #f3f4f6;
      }
      .shell {
        min-height: 100vh;
        display: grid;
        place-items: center;
        padding: 18px;
        box-sizing: border-box;
      }
      .card {
        width: min(780px, 100%);
        background: #171922;
        border: 1px solid #2a2f3f;
        border-radius: 12px;
        padding: 16px;
        box-sizing: border-box;
      }
      h3 {
        margin: 0 0 12px;
        font-size: 1.1rem;
      }
      .content {
        max-height: calc(100vh - 180px);
        overflow: auto;
      }
      .content a {
        color: #7dd3fc;
      }
      .footer {
        margin-top: 14px;
        display: flex;
        justify-content: flex-end;
      }
      .btn {
        border: 1px solid #3b4259;
        background: #111827;
        color: #f3f4f6;
        border-radius: 8px;
        padding: 8px 14px;
        font: inherit;
        cursor: pointer;
      }
      .btn:hover { border-color: #64748b; }
    </style>
  </head>
  <body>
    <div class="shell">
      <div class="card">
        <h3 id="note-title"></h3>
        <div id="note-content" class="content"></div>
        <div class="footer">
          <button id="note-ok" class="btn" type="button">Ok</button>
        </div>
      </div>
    </div>
  </body>
</html>`);
    popup.document.close();

    popup.document.title = `${linkName} Notes`;
    const titleEl = popup.document.getElementById('note-title');
    if (titleEl) titleEl.textContent = `${linkName} Notes`;

    const contentEl = popup.document.getElementById('note-content');
    if (contentEl) {
      contentEl.innerHTML = popupNoteHtml || '';
      const anchors = contentEl.querySelectorAll('a');
      anchors.forEach(anchor => {
        anchor.setAttribute('target', '_blank');
        anchor.setAttribute('rel', 'noreferrer');
      });
    }

    const okButton = popup.document.getElementById('note-ok');
    if (okButton) {
      if (options?.openDestinationInNewTab && options.destinationHref) {
        const linkButton = popup.document.createElement('a');
        linkButton.id = 'note-ok';
        linkButton.className = 'btn';
        linkButton.href = options.destinationHref;
        linkButton.target = '_blank';
        linkButton.rel = 'noreferrer';
        linkButton.textContent = 'Ok';
        linkButton.style.display = 'inline-flex';
        linkButton.style.alignItems = 'center';
        linkButton.style.justifyContent = 'center';
        linkButton.style.textDecoration = 'none';
        linkButton.addEventListener('click', () => {
          popup.setTimeout(() => {
            try {
              popup.close();
            } catch {}
          }, 150);
        });
        okButton.replaceWith(linkButton);
      } else {
        okButton.addEventListener('click', () => {
          popup.close();
        });
      }
    }

    popup.focus();
    return true;
  } catch {
    return false;
  }
}

export default function PublicExternalLink({
  linkName,
  href,
  openBehavior,
  showPopupNote,
  popupNoteHtml,
  clipboardText,
  className,
  style,
  children,
  onNavigate,
}: Props) {
  const pathname = usePathname() || '';

  const behavior = openBehavior === 'new' ? 'new' : 'current';
  const isAdminPath = pathname.startsWith('/admin');
  const noteEnabled = useMemo(() => {
    if (isAdminPath) return false;
    return Boolean(showPopupNote) && hasPopupNoteContent(popupNoteHtml);
  }, [isAdminPath, showPopupNote, popupNoteHtml]);
  const copyEnabled = useMemo(() => {
    if (isAdminPath) return false;
    return hasClipboardText(clipboardText);
  }, [clipboardText, isAdminPath]);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!href || href === '#') return;

    if (behavior === 'new') {
      const openedNote = openExternalNoteWindow(linkName, popupNoteHtml, {
        destinationHref: href,
        openDestinationInNewTab: true,
      });
      if (copyEnabled) {
        void writeClipboardText(clipboardText);
      }
      if (openedNote) {
        event.preventDefault();
      }
      return;
    }

    event.preventDefault();
    const openedNote = openExternalNoteWindow(linkName, popupNoteHtml);
    const continueNavigation = () => {
      window.setTimeout(() => {
        window.location.assign(href);
      }, openedNote ? 350 : 0);
    };
    if (copyEnabled) {
      void writeClipboardText(clipboardText).finally(continueNavigation);
      return;
    }
    continueNavigation();
  };

  const handleClipboardOnlyClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!href || href === '#') return;
    if (behavior === 'new') {
      void writeClipboardText(clipboardText);
      return;
    }
    event.preventDefault();
    void writeClipboardText(clipboardText).finally(() => {
      window.location.assign(href);
    });
  };

  const handleLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onNavigate?.();
    if (noteEnabled) {
      handleClick(event);
      return;
    }
    if (copyEnabled) {
      handleClipboardOnlyClick(event);
    }
  };

  const target = behavior === 'new' ? '_blank' : undefined;
  const rel = behavior === 'new' ? 'noreferrer' : undefined;

  return (
    <Link href={href || '#'} className={className} style={style} target={target} rel={rel} onClick={handleLinkClick}>
      {children}
    </Link>
  );
}
